import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { studiesLobbyApi } from '@/lib/api'
import type {
  StudiesLobbyItem,
  StudiesLobbyItemKind,
  StudiesLobbySortBy,
  StudiesLobbySortDirection,
  StudiesLobbyStats,
} from '@/types/routes/tests'
import { shouldRefreshExperiments } from './use-experiments-paginated'
import { useExperimentListRealtime } from '@/lib/realtime/useExperimentListRealtime'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'

/** Stable de-dup / removal key across the experiment + draft union. */
function lobbyItemKey(item: StudiesLobbyItem): string {
  return `${item.itemKind}:${item.id}`
}

/** Only experiment items can be mid-flight; drafts never poll. */
export function shouldRefreshLobby(items?: Array<StudiesLobbyItem>): boolean {
  const experimentItems = (items || []).filter(
    (item) => item.itemKind === 'experiment',
  )
  return shouldRefreshExperiments(experimentItems)
}

/**
 * Paginated Studies lobby hook. Loads the backend `GET /studies/lobby`
 * discriminated union (experiments + drafts) with explicit "load more" appends.
 *
 * Cache scope: per search term. Offset advances independently of page size.
 *
 * - `initialLimit`: number of items shown on first load (default 6)
 * - `pageSize`: number of items appended on each "load more" (default 5)
 * - Search is server-side: changing `search` resets offset and re-queries.
 */
export function useStudiesLobbyPaginated(
  initialLimit = 6,
  pageSize = 5,
  search = '',
  sortBy: StudiesLobbySortBy = 'date',
  sortDirection: StudiesLobbySortDirection = 'desc',
) {
  const trimmedSearch = search.trim()
  const [nextOffset, setNextOffset] = useState(0)
  const [currentLimit, setCurrentLimit] = useState(initialLimit)
  const [allItems, setAllItems] = useState<Array<StudiesLobbyItem>>([])
  const [hasMore, setHasMore] = useState(true)
  const [stats, setStats] = useState<StudiesLobbyStats | null>(null)
  const [total, setTotal] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const previousSearchRef = useRef(trimmedSearch)

  useEffect(() => {
    const hasSearchChanged = previousSearchRef.current !== trimmedSearch
    previousSearchRef.current = trimmedSearch

    setNextOffset(0)
    setCurrentLimit(initialLimit)
    if (hasSearchChanged) {
      setAllItems([])
    }
    setHasMore(true)
  }, [trimmedSearch, initialLimit, sortBy, sortDirection])

  const { data, isLoading, error, mutate } = useSWR(
    `studies-lobby-offset-${nextOffset}-${currentLimit}-${trimmedSearch}-${sortBy}-${sortDirection}`,
    () =>
      studiesLobbyApi.listPaginated(
        nextOffset,
        currentLimit,
        trimmedSearch || undefined,
        sortBy,
        sortDirection,
      ),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
      dedupingInterval: 500,
      refreshInterval: legacyRefreshInterval('STUDIES', 5000),
    },
  )

  const invalidate = useCallback(() => {
    void mutate()
  }, [mutate])

  useExperimentListRealtime({
    active: shouldRefreshLobby(data?.items ?? allItems),
    onInvalidate: invalidate,
  })

  useEffect(() => {
    if (!data?.items) return

    setAllItems((prev) => {
      if (nextOffset === 0) return data.items
      const existingKeys = new Set(prev.map(lobbyItemKey))
      const newItems = data.items.filter(
        (item) => !existingKeys.has(lobbyItemKey(item)),
      )
      return [...prev, ...newItems]
    })
    setHasMore(data.hasMore)
    setTotal(data.total)
    if (data.stats) setStats(data.stats)
    setIsLoadingMore(false)
  }, [data, nextOffset])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true)
      setNextOffset((prev) => prev + currentLimit)
      setCurrentLimit(pageSize)
    }
  }, [hasMore, isLoadingMore, currentLimit, pageSize])

  const reset = useCallback(() => {
    setNextOffset(0)
    setCurrentLimit(initialLimit)
    setAllItems([])
    setHasMore(true)
    mutate()
  }, [mutate, initialLimit])

  const removeItem = useCallback(
    (kind: StudiesLobbyItemKind, id: string) => {
      setAllItems((prev) =>
        prev.filter((item) => !(item.itemKind === kind && item.id === id)),
      )
      setTotal((prev) => Math.max(0, prev - 1))
    },
    [],
  )

  const stillProcessing =
    !isLoading &&
    data?.items &&
    allItems.length === 0 &&
    data.items.length > 0

  return {
    items: allItems,
    stats,
    total,
    isLoading: (isLoading && nextOffset === 0) || Boolean(stillProcessing),
    isLoadingMore: isLoadingMore || (isLoading && nextOffset > 0),
    hasMore,
    loadMore,
    reset,
    removeItem,
    error,
    mutate,
  }
}
